import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { UserSession } from '../types';
import { progressStore } from '../services/api';
import { Shield, Globe, Sun, Moon, LogOut, Award, LayoutDashboard, BookOpen, User as UserIcon, FileCheck, Settings } from 'lucide-react';

interface NavbarProps {
  session: UserSession | null;
  onLogout: () => void;
  theme: 'dark' | 'light';
  setTheme: (t: 'dark' | 'light') => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  session,
  onLogout,
  theme,
  setTheme,
}) => {
  const { lang, setLang, t } = useLanguage();
  const navigate = useNavigate();

  const userBadges = session ? progressStore.getBadges(session.userId) : [];
  const initials = session?.email ? session.email.substring(0, 2).toUpperCase() : 'US';

  const handleLogoutClick = () => {
    onLogout();
    navigate('/');
  };

  return (
    <nav className="responsive-navbar">
      {/* Brand Logo */}
      <NavLink
        to="/"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          cursor: 'pointer',
          textDecoration: 'none',
          color: 'inherit'
        }}
      >
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '10px',
          background: 'var(--gradient-brand)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 'var(--shadow-glow)'
        }}>
          <Shield size={22} color="#ffffff" />
        </div>
        <div>
          <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '20px', letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>
            e-learning <span style={{ color: 'var(--accent-primary)', fontSize: '13px', fontWeight: 600 }}>by akretrix</span>
          </span>
          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {t('Cumplimiento Corporativo', 'Corporate Compliance')}
          </div>
        </div>
      </NavLink>

      {/* Nav Links Group */}
      {session && (
        <div className="nav-links-group" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <NavLink
            to="/"
            className={({ isActive }) => `btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <BookOpen size={16} />
            {t('Cursos', 'Courses')}
          </NavLink>

          <NavLink
            to="/profile?tab=badges"
            className={({ isActive }) => `btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <Award size={16} />
            {t('Insignias', 'Badges')}
            {userBadges.length > 0 && (
              <span style={{
                background: 'rgba(16, 185, 129, 0.25)',
                color: '#34d399',
                border: '1px solid #10b981',
                borderRadius: '10px',
                fontSize: '11px',
                padding: '1px 6px',
                fontWeight: 700
              }}>
                {userBadges.length}
              </span>
            )}
          </NavLink>

          {session.role === 'B2B_ADMIN' && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <LayoutDashboard size={16} />
              {t('Panel B2B', 'B2B Admin')}
            </NavLink>
          )}

          <NavLink
            to="/verify"
            className={({ isActive }) => `btn ${isActive ? 'btn-primary' : 'btn-secondary'}`}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}
          >
            <FileCheck size={16} />
            {t('Verificar', 'Verify')}
          </NavLink>
        </div>
      )}

      {/* Controls (Language, Theme, User Profile) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {/* Language Switcher */}
        <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', padding: '2px' }}>
          <Globe size={14} style={{ margin: '0 4px', color: 'var(--text-muted)' }} />
          <button
            onClick={() => setLang('es')}
            style={{
              background: lang === 'es' ? 'var(--accent-primary)' : 'transparent',
              color: lang === 'es' ? '#ffffff' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 6px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            ES
          </button>
          <button
            onClick={() => setLang('en')}
            style={{
              background: lang === 'en' ? 'var(--accent-primary)' : 'transparent',
              color: lang === 'en' ? '#ffffff' : 'var(--text-secondary)',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 6px',
              fontSize: '11px',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            EN
          </button>
        </div>

        {/* Theme Switcher */}
        <button
          className="btn btn-secondary"
          style={{ padding: '6px 10px' }}
          onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
          title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {theme === 'dark' ? <Sun size={15} color="#f59e0b" /> : <Moon size={15} color="#3b82f6" />}
        </button>

        {/* User Avatar Chip -> Links to Profile Overview */}
        {session && (
          <NavLink
            to="/profile?tab=overview"
            style={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '4px 10px',
              background: 'var(--bg-secondary)',
              borderRadius: '20px',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              fontSize: '12px',
              fontWeight: 600
            }}
            title={t('Mi Perfil', 'My Profile')}
          >
            <div style={{
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              background: 'var(--gradient-brand)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 800
            }}>
              {initials}
            </div>
            <span className="hide-mobile" style={{ maxWidth: '110px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {session.email.split('@')[0]}
            </span>
          </NavLink>
        )}

        {/* Settings button */}
        {session && (
          <NavLink
            to="/profile?tab=settings"
            className="btn btn-secondary"
            style={{ padding: '6px 10px', textDecoration: 'none' }}
            title={t('Configuración', 'Settings')}
          >
            <Settings size={15} color="#a855f7" />
          </NavLink>
        )}

        {/* Logout button */}
        {session && (
          <button className="btn btn-secondary" style={{ padding: '6px 10px' }} onClick={handleLogoutClick} title="Logout">
            <LogOut size={15} />
          </button>
        )}
      </div>
    </nav>
  );
};
